/// Reusable composer for the dev-blog board — used for a new post, a
/// post edit, a new comment, and a comment edit. `kind === 'post'`
/// shows a title field (required, ≤120 chars); the body is required
/// and capped (5000 for posts, 3000 for comments). After a
/// successful submit it resets its fields and shows a brief "Posted
/// ✓" cooldown to blunt accidental double-submits; the *parent's*
/// `onSubmit` handler is responsible for any navigation/closing.

import { useState } from 'react'

const TITLE_MAX = 120
const POST_BODY_MAX = 5000
const COMMENT_BODY_MAX = 3000
const COOLDOWN_MS = 3000

const EMAIL_MAX = 254
// Light client-side plausibility check; the Cloud Function is the
// real validator.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i

export function DevBlogComposer(props: {
  kind: 'post' | 'comment'
  initialTitle?: string
  initialBody?: string
  submitLabel: string
  autoFocus?: boolean
  /// Show the optional "email me when the developer replies" field
  /// (new board posts only — the caller decides).
  notifyEmailField?: boolean
  onSubmit: (v: { title: string; body: string; notifyEmail?: string }) => Promise<void>
  onCancel?: () => void
}) {
  const bodyMax = props.kind === 'post' ? POST_BODY_MAX : COMMENT_BODY_MAX
  const [title, setTitle] = useState(props.initialTitle ?? '')
  const [body, setBody] = useState(props.initialBody ?? '')
  const [notifyEmail, setNotifyEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [cooldown, setCooldown] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const titleTrim = title.trim()
  const bodyTrim = body.trim()
  const emailTrim = notifyEmail.trim()
  const titleOk = props.kind !== 'post' || (titleTrim.length >= 1 && titleTrim.length <= TITLE_MAX)
  const bodyOk = bodyTrim.length >= 1 && bodyTrim.length <= bodyMax
  const emailOk = !props.notifyEmailField || emailTrim.length === 0 || EMAIL_RE.test(emailTrim)
  const canSubmit = titleOk && bodyOk && emailOk && !busy && !cooldown

  const submit = async () => {
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      await props.onSubmit({
        title: titleTrim,
        body: bodyTrim,
        notifyEmail: props.notifyEmailField && emailTrim.length > 0 ? emailTrim : undefined,
      })
      setTitle(props.initialTitle ?? '')
      setBody(props.initialBody ?? '')
      setNotifyEmail('')
      setCooldown(true)
      window.setTimeout(() => setCooldown(false), COOLDOWN_MS)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      {props.kind === 'post' && (
        <input
          type="text"
          value={title}
          maxLength={TITLE_MAX}
          autoFocus={props.autoFocus}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Short summary…"
          className="w-full rounded-md bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-emerald-500"
        />
      )}
      <textarea
        value={body}
        rows={props.kind === 'post' ? 5 : 3}
        autoFocus={props.kind === 'comment' && props.autoFocus}
        onChange={(e) => setBody(e.target.value.slice(0, bodyMax))}
        placeholder={props.kind === 'post' ? 'Ask a question or share feedback…' : 'Write a reply…'}
        className="w-full resize-y rounded-md bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-emerald-500"
      />
      {props.notifyEmailField && props.kind === 'post' && (
        <div className="space-y-1">
          <input
            type="email"
            value={notifyEmail}
            maxLength={EMAIL_MAX}
            onChange={(e) => setNotifyEmail(e.target.value)}
            placeholder="Email (optional): hear back when the developer replies"
            className={
              'w-full rounded-md bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-1 focus:ring-emerald-500 ' +
              (emailOk ? 'ring-white/10' : 'ring-rose-500/60')
            }
          />
          <p className="text-[11px] leading-snug text-slate-500">
            Stored privately, used once per developer reply, never shown on the board. Deleted with the post.
          </p>
        </div>
      )}
      {error && (
        <div className="rounded-md bg-rose-900/50 px-3 py-2 text-xs text-rose-100 ring-1 ring-rose-500/30">{error}</div>
      )}
      <div className="flex items-center justify-end gap-3 text-xs text-slate-500">
        <span className="tabular-nums">
          {bodyTrim.length}/{bodyMax}
        </span>
        {props.onCancel && (
          <button type="button" onClick={props.onCancel} disabled={busy} className="text-slate-400 hover:text-white">
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className={
            'rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white ' +
            (canSubmit ? 'hover:bg-emerald-500' : 'opacity-50')
          }
        >
          {busy ? 'Posting…' : cooldown ? 'Posted ✓' : props.submitLabel}
        </button>
      </div>
    </div>
  )
}
